import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source';
import { ApiError, type ApiClient } from './client';
import { connection } from './connection';
import type { Run, RunEvent } from './types';

export interface RunEventStreamHandlers {
  onEvent: (event: RunEvent) => void;
  /** The run reached a terminal status; the stream is closed. */
  onCompleted: (run: Run) => void;
  /** The connection dropped and will be retried. */
  onReconnecting?: () => void;
}

export interface RunEventStreamOptions {
  /** Sequence to start after (events already loaded). */
  after?: number;
  signal: AbortSignal;
  retryIntervalMs?: number;
}

class FatalStreamError extends Error {
  readonly inner: unknown;

  constructor(inner: unknown) {
    super('The run event stream failed.');
    this.inner = inner;
  }
}

class RetriableStreamError extends Error {}

/**
 * Follows `GET /runs/{id}/events` as Server-Sent Events. Reconnects with `Last-Event-ID`
 * (the last sequence received) until `run-completed` arrives or the signal aborts.
 * Resolves when the stream ends; rejects with an ApiError on a client error (4xx).
 */
export async function streamRunEvents(
  client: ApiClient,
  runId: string,
  handlers: RunEventStreamHandlers,
  options: RunEventStreamOptions,
): Promise<void> {
  let lastSequence = options.after ?? 0;
  let completed = false;
  const retryInterval = options.retryIntervalMs ?? 2000;
  // Identifies this stream in the status bar's connection state ("Reconnecting…").
  const stream = {};

  const fetchWithResume: typeof fetch = (input, init) => {
    const headers = new Headers(init?.headers);
    headers.set('Accept', 'text/event-stream');
    if (lastSequence > 0) headers.set('Last-Event-ID', String(lastSequence));
    else headers.delete('Last-Event-ID');
    return client.authorizedFetch(input, { ...init, headers });
  };

  try {
    await fetchEventSource(client.url(`/runs/${runId}/events`, { after: options.after ?? 0 }), {
      method: 'GET',
      signal: options.signal,
      // Hidden tabs close the stream and resume with Last-Event-ID when visible again (spec 6.8.12).
      openWhenHidden: false,
      fetch: fetchWithResume,
      async onopen(response) {
        const contentType = response.headers.get('Content-Type') ?? '';
        if (response.ok && contentType.startsWith('text/event-stream')) {
          connection.streamConnected(stream);
          return;
        }
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new FatalStreamError(await ApiError.fromResponse(response));
        }
        throw new RetriableStreamError(`Unexpected response ${response.status}`);
      },
      onmessage(message: EventSourceMessage) {
        switch (message.event) {
          case 'run-event': {
            const event = JSON.parse(message.data) as RunEvent;
            if (event.sequence > lastSequence) lastSequence = event.sequence;
            handlers.onEvent(event);
            break;
          }
          case 'run-completed':
            completed = true;
            handlers.onCompleted(JSON.parse(message.data) as Run);
            break;
          default:
            // keep-alive and unknown events are ignored.
            break;
        }
      },
      onclose() {
        if (!completed) throw new RetriableStreamError('The stream closed before the run completed.');
      },
      onerror(error) {
        if (error instanceof FatalStreamError) throw error;
        connection.streamRetrying(stream);
        handlers.onReconnecting?.();
        return retryInterval;
      },
    });
  } catch (error) {
    if (error instanceof FatalStreamError) throw error.inner;
    throw error;
  } finally {
    connection.streamConnected(stream);
  }
}
